import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Gift, Copy, Share2, CheckCircle, Users, Star } from 'lucide-react';
import { toast } from 'sonner';
import BottomNavigation from '@/components/BottomNavigation';

interface ReferralReward {
  id: string;
  reward_type: 'signup_bonus' | 'order_1_bonus' | 'order_2_bonus';
  reward_code: string | null;
  discount_amount: number;
  is_used: boolean;
  created_at: string;
}

interface ProfileData {
  referral_code: string | null;
  full_name: string | null;
}

const rewardTypeLabel = (type: string) => {
  switch (type) {
    case 'signup_bonus': return 'Welcome Bonus 🎁';
    case 'order_1_bonus': return "Friend's 1st Order 🎉";
    case 'order_2_bonus': return "Friend's 2nd Order 🔥";
    default: return 'Referral Reward';
  }
};

const ReferralPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);
  const [copiedRewardId, setCopiedRewardId] = useState<string | null>(null);
  const [referralCount, setReferralCount] = useState(0);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const [profileRes, rewardsRes, countRes] = await Promise.all([
        supabase.from('profiles').select('referral_code, full_name').eq('id', user.id).single(),
        supabase.from('referral_rewards').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
        supabase.from('profiles').select('id').eq('referred_by', user.id)
      ]);

      if (profileRes.data) setProfile(profileRes.data);
      if (rewardsRes.data) setRewards(rewardsRes.data as ReferralReward[]);
      if (countRes.data) setReferralCount(countRes.data.length);
    } catch (err) {
      console.error('Error fetching referral data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (!profile?.referral_code) return;
    try {
      await navigator.clipboard.writeText(profile.referral_code);
      setCodeCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCodeCopied(false), 2500);
    } catch {
      toast.error('Could not copy code');
    }
  };

  const handleShare = async () => {
    if (!profile?.referral_code) return;
    const shareText = `Hi, enjoy D50 off your first order on GAMBGO using my referral code "${profile.referral_code}" — signup and start ordering here: getgambgo.com`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join GAMBGO & get D50 off!', text: shareText, url: 'https://getgambgo.com' });
      } catch {
        // user cancelled share
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success('Share message copied to clipboard!');
      } catch {
        toast.error('Could not copy share message');
      }
    }
  };

  const handleCopyRewardCode = async (reward: ReferralReward) => {
    if (!reward.reward_code) return;
    try {
      await navigator.clipboard.writeText(reward.reward_code);
      setCopiedRewardId(reward.id);
      toast.success(`Code ${reward.reward_code} copied!`);
      setTimeout(() => setCopiedRewardId(null), 2500);
    } catch {
      toast.error('Could not copy code');
    }
  };

  const earnedRewards = rewards.filter(r => r.reward_type !== 'signup_bonus');
  const totalEarned = earnedRewards.length * 50;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-5 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold">Refer & Earn</h1>
      </div>

      <div className="px-4 py-6 space-y-6">

        {/* Hero referral card */}
        <div className="relative rounded-2xl overflow-hidden shadow-xl">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-red-500 to-orange-500" />
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-10 -left-10 w-52 h-52 rounded-full bg-white/10" />
          <div className="absolute top-4 right-4 w-20 h-20 rounded-full bg-white/5" />

          <div className="relative z-10 p-6 text-white">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="h-5 w-5" />
              <span className="font-bold text-lg">Refer & Earn D50</span>
            </div>
            <p className="text-white/80 text-sm mb-6">
              Share your code — friends get D50 off their first order, and you earn D50 for every order they place.
            </p>

            {/* Code display with shimmer */}
            <div className="relative bg-white/20 backdrop-blur-sm rounded-xl p-4 mb-4 border border-white/30">
              <p className="text-white/70 text-xs uppercase tracking-widest mb-1">Your code</p>
              <div className="flex items-center justify-between">
                <span className="text-3xl font-black tracking-widest text-white">
                  {profile?.referral_code ?? '—'}
                </span>
                <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2.5s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleCopyCode}
                className="flex-1 h-11 font-semibold transition-all duration-200"
                style={{
                  background: codeCopied ? '#22c55e' : 'rgba(255,255,255,0.25)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: 'white',
                  backdropFilter: 'blur(8px)'
                }}
              >
                {codeCopied ? (
                  <><CheckCircle className="h-4 w-4 mr-2" /> Copied!</>
                ) : (
                  <><Copy className="h-4 w-4 mr-2" /> Copy Code</>
                )}
              </Button>
              <Button
                onClick={handleShare}
                className="flex-1 h-11 font-semibold"
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: 'white',
                  backdropFilter: 'blur(8px)'
                }}
              >
                <Share2 className="h-4 w-4 mr-2" /> Share
              </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-4 mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-white/70" />
                <span className="text-white/90 text-sm">{referralCount} friend{referralCount !== 1 ? 's' : ''} joined</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-white/70" />
                <span className="text-white/90 text-sm">D{totalEarned} earned</span>
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="font-bold text-gray-900 mb-4">How it works</h3>
          <div className="space-y-4">
            {[
              { step: '1', text: 'Share your unique code with friends', icon: '📤' },
              { step: '2', text: 'Friend signs up & gets D50 off their first order automatically', icon: '🎁' },
              { step: '3', text: 'They place their 1st order → you get a D50 reward code', icon: '💸' },
              { step: '4', text: 'They place their 2nd order → you get another D50 reward code', icon: '🔥' },
            ].map(({ step, text, icon }) => (
              <div key={step} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-red-100 text-red-600 font-bold text-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                  {step}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{icon} {text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Your rewards */}
        <div>
          <h3 className="font-bold text-gray-900 mb-3">Your Rewards</h3>
          {earnedRewards.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
              <div className="text-4xl mb-3">🎯</div>
              <p className="text-gray-500 font-medium">No rewards yet</p>
              <p className="text-gray-400 text-sm mt-1">Share your code and start earning! Rewards appear here once your friends place orders.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {earnedRewards.map((reward, i) => (
                <div
                  key={reward.id}
                  className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center justify-between"
                  style={{ animation: `fadeInUp 0.3s ease ${i * 0.08}s both` }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-800">{rewardTypeLabel(reward.reward_type)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${reward.is_used ? 'bg-gray-100 text-gray-400' : 'bg-green-100 text-green-700'}`}>
                        {reward.is_used ? 'Used' : 'Available'}
                      </span>
                    </div>
                    <div className={`font-mono font-bold text-lg tracking-widest ${reward.is_used ? 'text-gray-300 line-through' : 'text-red-600'}`}>
                      {reward.reward_code}
                    </div>
                    <p className="text-gray-400 text-xs mt-0.5">
                      D{reward.discount_amount} off · {new Date(reward.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {!reward.is_used && reward.reward_code && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyRewardCode(reward)}
                      className="ml-3 flex-shrink-0"
                    >
                      {copiedRewardId === reward.id ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <BottomNavigation />
    </div>
  );
};

export default ReferralPage;
